/**
 * Shared browser/API connectivity status for UI banner + HTTP error handling.
 * Status changes are the only signal; callers must not toast on every failed request.
 */

export const CONNECTIVITY = {
  ONLINE: "online",
  OFFLINE: "offline",
  DEGRADED: "degraded",
};

const RESTORE_DEBOUNCE_MS = 1000;

let state = {
  status:
    typeof navigator !== "undefined" && !navigator.onLine
      ? CONNECTIVITY.OFFLINE
      : CONNECTIVITY.ONLINE,
  lastChangedAt: null,
  lastCheckedAt: null,
};

const listeners = new Set();
let restoreTimer = null;

function notify() {
  const snapshot = getConnectivity();
  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (err) {
      console.error("connectivity listener failed", err);
    }
  });
}

function applyStatus(status, { checkedAt = Date.now() } = {}) {
  const prev = state.status;
  state = {
    ...state,
    status,
    lastCheckedAt: checkedAt,
    lastChangedAt: status !== prev ? checkedAt : state.lastChangedAt,
  };
  if (status !== prev) notify();
}

export function getConnectivity() {
  return { ...state };
}

export function subscribeConnectivity(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function reportBrowserOffline() {
  if (restoreTimer) {
    clearTimeout(restoreTimer);
    restoreTimer = null;
  }
  applyStatus(CONNECTIVITY.OFFLINE);
}

export function reportApiUnreachable() {
  if (restoreTimer) {
    clearTimeout(restoreTimer);
    restoreTimer = null;
  }
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    applyStatus(CONNECTIVITY.OFFLINE);
    return;
  }
  applyStatus(CONNECTIVITY.DEGRADED);
}

/**
 * Mark connectivity as healthy. Debounced so brief flaps don't flash the UI.
 * Pass { immediate: true } after an explicit user-triggered health check.
 */
export function reportOnline({ immediate = false } = {}) {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    reportBrowserOffline();
    return;
  }

  if (state.status === CONNECTIVITY.ONLINE) {
    state = { ...state, lastCheckedAt: Date.now() };
    return;
  }

  if (immediate) {
    if (restoreTimer) {
      clearTimeout(restoreTimer);
      restoreTimer = null;
    }
    applyStatus(CONNECTIVITY.ONLINE);
    return;
  }

  if (restoreTimer) return;
  restoreTimer = setTimeout(() => {
    restoreTimer = null;
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      applyStatus(CONNECTIVITY.OFFLINE);
      return;
    }
    applyStatus(CONNECTIVITY.ONLINE);
  }, RESTORE_DEBOUNCE_MS);
}

export function isTransportError(error) {
  if (!error) return false;
  if (error.response) return false;
  // Axios cancels / aborts are not connectivity failures.
  if (error.code === "ERR_CANCELED" || error.name === "CanceledError") return false;
  return true;
}
